<template>
  <div class="relative">
    <div ref="viewport" class="overflow-hidden rounded-2xl border border-gray-100 bg-gray-50 shadow-sm">
      <div class="flex">
        <div
          v-for="(src, idx) in images"
          :key="src"
          class="relative min-w-0 flex-[0_0_100%]"
        >
          <img
            :src="src"
            :alt="`${alt} ${idx + 1}`"
            class="block aspect-[16/9] w-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </div>

    <button
      v-if="canScrollPrev"
      type="button"
      class="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-800 shadow ring-1 ring-gray-200 backdrop-blur transition hover:bg-white"
      :aria-label="$t('locations.details.prev_image')"
      @click="scrollPrev"
    >
      <UIcon name="i-heroicons-chevron-left" class="h-5 w-5" />
    </button>
    <button
      v-if="canScrollNext"
      type="button"
      class="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-800 shadow ring-1 ring-gray-200 backdrop-blur transition hover:bg-white"
      :aria-label="$t('locations.details.next_image')"
      @click="scrollNext"
    >
      <UIcon name="i-heroicons-chevron-right" class="h-5 w-5" />
    </button>

    <div
      v-if="images.length > 1"
      class="mt-3 flex items-center justify-center gap-2"
    >
      <button
        v-for="(_, idx) in images"
        :key="idx"
        type="button"
        class="h-2 w-2 rounded-full transition-all"
        :class="idx === selectedIndex ? 'bg-primary-600 w-5' : 'bg-gray-300 hover:bg-gray-400'"
        :aria-label="`Go to slide ${idx + 1}`"
        @click="scrollTo(idx)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from 'vue'
import EmblaCarousel, { type EmblaCarouselType } from 'embla-carousel'

const props = withDefaults(
  defineProps<{
    images: string[]
    alt?: string
  }>(),
  { alt: 'Location image' }
)

const viewport = ref<HTMLElement | null>(null)
let embla: EmblaCarouselType | null = null

const selectedIndex = ref(0)
const canScrollPrev = ref(false)
const canScrollNext = ref(false)

const updateState = () => {
  if (!embla) return
  selectedIndex.value = embla.selectedScrollSnap()
  canScrollPrev.value = embla.canScrollPrev()
  canScrollNext.value = embla.canScrollNext()
}

const scrollPrev = () => embla?.scrollPrev()
const scrollNext = () => embla?.scrollNext()
const scrollTo = (idx: number) => embla?.scrollTo(idx)

onMounted(() => {
  if (!viewport.value) return
  embla = EmblaCarousel(viewport.value, {
    loop: props.images.length > 1,
    align: 'start',
    skipSnaps: false
  })
  embla.on('select', updateState)
  embla.on('reInit', updateState)
  updateState()
})

onBeforeUnmount(() => {
  embla?.destroy()
  embla = null
})
</script>
